import { useState, useEffect, useRef, useCallback, type Dispatch } from 'react'
import {
  getDrafts,
  getDraft,
  saveDraft,
  deleteDraft,
  createDraft,
  getCurrentDraft,
  type Draft,
} from '@/lib/storage/local-storage'
import { detectFormatFromFilename } from '@/lib/utils/filename'
import type { ShareFormat } from '@/types/share'
import type { EditorState } from '../state'
import type { EditorAction } from '../state'
import type { ViewMode } from '../EditorHeader'

const AUTOSAVE_DEBOUNCE_MS = 300

interface UseEditorDraftsOptions {
  readOnly: boolean
  initialContent: string
  initialFilename: string
  defaultViewMode: ViewMode
  viewingShareIdProp?: string
  viewingShareMetaProp?: { isModifiable: boolean; managementToken?: string; burnAfterRead?: boolean; expiresAt?: number }
}

/**
 * Manages draft lifecycle: initialization, autosave, CRUD operations, and context switching.
 * This is the largest hook — it owns the drafts list and all draft-related side effects.
 */
export function useEditorDrafts(
  state: EditorState,
  dispatch: Dispatch<EditorAction>,
  options: UseEditorDraftsOptions,
) {
  const { readOnly, initialContent, initialFilename, defaultViewMode, viewingShareIdProp, viewingShareMetaProp } = options

  const [drafts, setDrafts] = useState<Draft[]>([])
  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isInitializedRef = useRef(false)

  // Ref for autosave to read latest state without stale closures
  const stateRef = useRef(state)
  stateRef.current = state

  const refreshDrafts = useCallback(() => {
    setDrafts(getDrafts())
  }, [])

  // Initialize draft on mount (only in edit mode, not when initial content is provided)
  useEffect(() => {
    if (readOnly || isInitializedRef.current) return
    isInitializedRef.current = true

    if (viewingShareIdProp && viewingShareMetaProp?.isModifiable) {
      dispatch({ type: 'SET_FILE_TYPE', fileType: 'protected' })
      refreshDrafts()
      return
    }

    if (initialContent) {
      const draft = createDraft(initialFilename)
      saveDraft({ ...draft, content: initialContent })
      dispatch({ type: 'SET_CURRENT_DRAFT_ID', draftId: draft.id })
      refreshDrafts()
      return
    }

    const draft = getCurrentDraft()
    if (draft) {
      dispatch({ type: 'LOAD_DRAFT', draftId: draft.id, content: draft.content, filename: draft.filename })
    }
    refreshDrafts()
  }, [readOnly, initialContent, initialFilename, refreshDrafts, viewingShareIdProp, viewingShareMetaProp, dispatch])

  // Listen for drafts-changed events
  useEffect(() => {
    const handleDraftsChanged = () => refreshDrafts()
    window.addEventListener('drafts-changed', handleDraftsChanged)
    return () => window.removeEventListener('drafts-changed', handleDraftsChanged)
  }, [refreshDrafts])

  // Listen for API key changes (sign-in/sign-out) to switch draft context
  useEffect(() => {
    if (readOnly) return

    const handleApiKeyChanged = () => {
      const availableDrafts = getDrafts()

      if (availableDrafts.length === 0) {
        dispatch({ type: 'RESET_EDITOR' })
        refreshDrafts()
      } else {
        const currentDraftStillExists = availableDrafts.some(d => d.id === stateRef.current.currentDraftId)

        if (!currentDraftStillExists) {
          const first = availableDrafts[0]!
          dispatch({ type: 'RESET_EDITOR' })
          dispatch({ type: 'LOAD_DRAFT', draftId: first.id, content: first.content, filename: first.filename })
        }
        refreshDrafts()
      }
    }

    window.addEventListener('apikey-changed', handleApiKeyChanged)
    return () => window.removeEventListener('apikey-changed', handleApiKeyChanged)
  }, [readOnly, refreshDrafts, dispatch])

  // Auto-save draft with debounce
  useEffect(() => {
    if (readOnly) return

    const { content, originalContent, viewingShareId, viewingShareMeta, currentDraftId, filename } = state

    if (viewingShareId && content === originalContent) return
    if (viewingShareId && viewingShareMeta?.isModifiable) return

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      if (!currentDraftId) {
        if (content !== originalContent) {
          const newDraft = createDraft(filename)
          saveDraft({ ...newDraft, content })
          dispatch({ type: 'DETACH_FROM_SHARE', draftId: newDraft.id, filename: newDraft.filename })
          refreshDrafts()
        }
        return
      }

      const draft = getDraft(currentDraftId)
      if (draft) {
        saveDraft({
          ...draft,
          content,
          filename,
          updatedAt: Date.now(),
        })
      }
    }, AUTOSAVE_DEBOUNCE_MS)

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }
    }
  }, [state.content, state.filename, state.currentDraftId, readOnly, state.viewingShareId, state.viewingShareMeta, state.originalContent, refreshDrafts, dispatch])

  // Sync state when initial props change
  useEffect(() => {
    if (initialContent) {
      dispatch({ type: 'SET_CONTENT', content: initialContent })
      dispatch({ type: 'SET_ORIGINAL_CONTENT', originalContent: initialContent })
    }
  }, [initialContent, dispatch])

  useEffect(() => {
    if (initialContent && initialFilename !== 'untitled') {
      dispatch({ type: 'SET_FILENAME', filename: initialFilename })
    }
  }, [initialFilename, initialContent, dispatch])

  // --- Handlers ---

  const saveCurrentDraftNow = () => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
      autosaveTimeoutRef.current = null
    }

    const { currentDraftId, content, filename } = stateRef.current
    if (currentDraftId) {
      const draft = getDraft(currentDraftId)
      if (draft) {
        saveDraft({
          ...draft,
          content,
          filename,
          updatedAt: Date.now(),
        })
      }
    }
  }

  const handleNewFile = () => {
    saveCurrentDraftNow()

    const draft = createDraft()
    dispatch({ type: 'LOAD_DRAFT', draftId: draft.id, content: draft.content, filename: draft.filename })
    dispatch({ type: 'SET_VIEW_MODE', viewMode: defaultViewMode })
    refreshDrafts()
  }

  const handleSelectDraft = (draftId: string) => {
    saveCurrentDraftNow()

    const draft = getDraft(draftId)
    if (!draft) return

    dispatch({ type: 'RESET_EDITOR' })
    dispatch({ type: 'LOAD_DRAFT', draftId: draft.id, content: draft.content, filename: draft.filename })
    dispatch({ type: 'SET_VIEW_MODE', viewMode: defaultViewMode })
  }

  const handleDeleteDraft = (draftId: string) => {
    deleteDraft(draftId)

    if (draftId === state.currentDraftId) {
      const remainingDrafts = getDrafts()
      if (remainingDrafts.length > 0) {
        const first = remainingDrafts[0]!
        dispatch({ type: 'LOAD_DRAFT', draftId: first.id, content: first.content, filename: first.filename })
      } else {
        dispatch({ type: 'RESET_EDITOR' })
      }
    }
    refreshDrafts()
  }

  const handleRenameDraft = (draftId: string, newFilename: string) => {
    const draft = getDraft(draftId)
    if (!draft) return

    const newFormat = detectFormatFromFilename(newFilename)

    saveDraft({
      ...draft,
      filename: newFilename,
      formatOptions: { format: newFormat },
    })

    if (draftId === state.currentDraftId) {
      dispatch({ type: 'SET_FILENAME', filename: newFilename })
      dispatch({ type: 'SET_FORMAT_OPTIONS', options: { format: newFormat } })
    }
    refreshDrafts()
  }

  const handleContentLoaded = (newContent: string, newFilename?: string, newFormat?: ShareFormat) => {
    const draft = createDraft(newFilename || 'untitled', newFormat || 'md')
    const draftToSave = newFormat
      ? { ...draft, content: newContent, formatOptions: { format: newFormat } }
      : { ...draft, content: newContent }
    saveDraft(draftToSave)

    dispatch({
      type: 'LOAD_CONTENT',
      draftId: draft.id,
      content: newContent,
      filename: draft.filename,
      format: newFormat,
    })
    dispatch({ type: 'SET_VIEW_MODE', viewMode: defaultViewMode })
    refreshDrafts()
  }

  const handleCloseFile = () => {
    saveCurrentDraftNow()
    dispatch({ type: 'RESET_EDITOR' })
  }

  return {
    drafts,
    refreshDrafts,
    saveCurrentDraftNow,
    handleNewFile,
    handleSelectDraft,
    handleDeleteDraft,
    handleRenameDraft,
    handleContentLoaded,
    handleCloseFile,
  }
}
